import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const OPTION_KEYS = ["braille","low_vision","hearing_support","dyslexia","motor_support","voice_first","high_contrast","large_text","reduced_motion"];

export function applyAccessibility(a) {
  if (!a) return;
  document.body.classList.toggle("hb-high-contrast", !!a.high_contrast);
  document.body.classList.toggle("hb-large-text", !!a.large_text);
  document.body.classList.toggle("hb-dyslexia", !!a.dyslexia);
  document.body.classList.toggle("hb-reduced-motion", !!a.reduced_motion);
  if (a.font_size) document.body.style.fontSize = `${a.font_size}px`;
}

export default function AccessibilitySettings() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const initial = user?.profile?.accessibility || { font_size: 16 };
  const [a, setA] = useState({
    braille: !!initial.braille,
    low_vision: !!initial.low_vision,
    hearing_support: !!initial.hearing_support,
    dyslexia: !!initial.dyslexia,
    motor_support: !!initial.motor_support,
    voice_first: !!initial.voice_first,
    high_contrast: !!initial.high_contrast,
    large_text: !!initial.large_text,
    reduced_motion: !!initial.reduced_motion,
    font_size: initial.font_size || 16,
  });

  useEffect(() => { applyAccessibility(a); }, [a]);

  async function save() {
    try {
      await api.put("/auth/accessibility", a);
      applyAccessibility(a);
      await refresh();
      toast.success(t("access.saved"));
    } catch (e) {
      toast.error(t("errors.generic"));
    }
  }

  return (
    <AppShell>
      <h1 className="font-adult font-bold text-3xl text-slate-900">{t("access.setupTitle")}</h1>
      <p className="text-slate-600 mt-1">{t("access.setupSubtitle")}</p>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {OPTION_KEYS.map((k) => (
          <div key={k} className="hb-card p-5 flex items-center justify-between">
            <Label htmlFor={k} className="text-slate-800 font-medium">{t(`access.${k}`)}</Label>
            <Switch id={k} data-testid={`access-toggle-${k}`}
                    checked={a[k]} onCheckedChange={(v) => setA((s) => ({ ...s, [k]: v }))} />
          </div>
        ))}
        <div className="hb-card p-5 md:col-span-2">
          <Label className="text-slate-800 font-medium">{t("access.fontSize", { px: a.font_size })}</Label>
          <Slider className="mt-3" min={14} max={28} step={1}
                  value={[a.font_size]} onValueChange={(v) => setA((s) => ({ ...s, font_size: v[0] }))}
                  data-testid="access-slider-fontsize" />
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} data-testid="access-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">{t("common.save")}</Button>
      </div>
    </AppShell>
  );
}
